/**
 * PORT (mobile performance): damage rendering. A frame in which only a few small things changed (the menus' looping
 * animations: a shop sign, a blinking button, a counter) redraws only the device rectangles those things covered
 * before and cover now, clipped to them, instead of the whole canvas: the pixels outside keep the last frame's, which
 * is what a whole redraw would draw there again. A phone's main thread and GPU then do a fraction of the work on
 * screens that barely move, which is where most of a session's time (and a phone's heat) goes.
 *
 * What changed comes from the display list itself (render-data trackChanges): every visual setter, timeline change and
 * child list change reports its object (DAMAGE_SELF: its own state, DAMAGE_CHILDREN: its child list or frame), and a
 * BitmapData its pixels. Where things were drawn is the tracker's record: after a whole frame it measures every shown
 * object's device box (the same geometry the renderer draws: shape rasters, bitmaps, text fields, graphics, filter
 * padding, scroll rects), after a partial one the changed subtrees again (ancestors' records only grow: a record is
 * always a superset of what is on the canvas). A change's damage is its record before (the old place) and its box now;
 * a child list change damages the children removed and added, not the container; bitmap pixels damage the changed
 * texels' device box. Boxes are padded (anti-aliasing, pixel snapping, the filters of the ancestors) and rounded to
 * whole device pixels: the clip then has no partially covered pixels, and each pixel inside is drawn exactly as in a
 * whole frame (same transforms, same order, the same background fill first).
 *
 * A whole frame is drawn instead when the view changed (resize, dynamic resolution, the shown area, the stage colour),
 * when the host asks (an image decoded, a focused input's caret, the canvas cleared), while the renderer's caches
 * change pixels of objects that did not change (device-resolution tiles being built), when the stage itself changed,
 * and when the damage is large (over half the canvas: a battle's camera moving). After a few large damages in a row
 * tracking pauses for a while (no measuring in battles that redraw everything anyway).
 */
import * as rd from '../display/render-data';
import type { DisplayObject } from '../display/DisplayObject';
import { DAMAGE_CHILDREN, DAMAGE_SELF } from '../display/DisplayObject';
import { Box, Mat, mul, rectBox, translate } from './math';
import { filterPadding, filterSpec } from './filters';

/** Device px around every measured box: anti-aliased edges, pixel snapping of bitmaps, rounding of positions. */
const PAD = 2;
/** At most this many separate rectangles are clipped to (more are merged). */
const MAX_RECTS = 8;
/** Rectangles closer than this (device px) are merged: one clip rectangle is cheaper than two draws of the same art. */
const MERGE_GAP = 16;
/** Damage over this share of the canvas is drawn whole. */
const MAX_AREA = 0.5;
/** Whole frames in a row for their damage's size before tracking pauses, and for how many renders. */
const SUSPEND_AFTER = 3;
const SUSPEND_RENDERS = 30;
/** More changed objects than this in a frame: drawn whole without looking further. */
const MAX_CHANGES = 400;

export interface DamagePlan {
  /** Device rectangles to redraw (disjoint enough, whole pixels, inside the canvas). */
  rects: Box[];
  /** Their bounding box. */
  bbox: Box;
}

export interface DamageStats {
  /** Renders drawn in part / whole since the tracker was created. */
  partial: number;
  full: number;
  /** Whole renders while tracking paused. */
  suspended: number;
  /** The last render: share of the canvas redrawn (1: whole), rectangles, and why a whole frame was drawn. */
  lastArea: number;
  lastRects: number;
  lastReason: string;
}

const union = (a: Box | null, b: Box | null): Box | null =>
  !a ? b : !b ? a : { x0: Math.min(a.x0, b.x0), y0: Math.min(a.y0, b.y0), x1: Math.max(a.x1, b.x1), y1: Math.max(a.y1, b.y1) };
const area = (b: Box) => Math.max(0, b.x1 - b.x0) * Math.max(0, b.y1 - b.y0);

export class DamageTracker {
  /** Records of this epoch are valid (a whole frame's measure starts a new one). */
  private epoch = 0;

  /**
   * Whether `o` (with everything under it) draws nothing inside `rects` in this frame: its record is of this epoch
   * and misses them all (a record covers at least what the object draws on the canvas now, after plan()).
   */
  outside(o: DisplayObject, rects: readonly Box[]): boolean {
    if (o.$dgen !== this.epoch) return false;
    const b = o.$dbox;
    if (!b) return true;
    for (let i = 0; i < rects.length; i++) {
      const r = rects[i];
      if (b.x0 < r.x1 && b.x1 > r.x0 && b.y0 < r.y1 && b.y1 > r.y0) return false;
    }
    return true;
  }
  private valid = false;
  private viewKey = '';
  /** BitmapData -> the objects that draw it (Bitmaps, graphics with bitmap fills), from the measures of this epoch. */
  private bitmapUsers = new Map<object, Set<DisplayObject>>();
  private bigInRow = 0;
  private pause = 0;
  /** Device px per stage px of the view (filter padding). */
  private fsx = 1;
  private fsy = 1;
  /** The rectangles of the last partial frame (diagnostics, tests). */
  lastRects: Box[] = [];
  readonly stats: DamageStats = { partial: 0, full: 0, suspended: 0, lastArea: 1, lastRects: 0, lastReason: 'first' };

  constructor() {
    rd.trackChanges(true);
  }

  /** Stops tracking (the runtime no longer collects changes). */
  dispose(): void {
    rd.trackChanges(false);
    this.valid = false;
  }

  /** The canvas was drawn by someone else (or lost): the next frame is drawn whole. */
  invalidate(): void {
    this.valid = false;
  }

  /**
   * The plan of this frame: the rectangles to redraw, or null to draw it whole (then the tracker measures the whole
   * stage for the next frames). `vm`: stage -> device; `viewKey`: everything else a whole frame depends on; `force`:
   * the host wants a whole frame; `unstable`: the renderer's caches change pixels now.
   */
  plan(stage: DisplayObject, vm: Mat, W: number, H: number, viewKey: string, force: boolean, unstable: boolean): DamagePlan | null {
    rd.trackChanges(true);
    const ch = rd.takeChanges();
    this.fsx = Math.abs(vm[0]) || 1;
    this.fsy = Math.abs(vm[3]) || 1;
    let why = '';
    if (force) why = 'host';
    else if (viewKey !== this.viewKey) why = 'view';
    else if (!this.valid) why = this.pause > 0 ? 'paused' : 'records';
    else if (unstable) why = 'caches';
    else if (ch.objects.length > MAX_CHANGES) why = 'changes';
    let rects: Box[] | null = null;
    if (!why) {
      rects = this.damage(ch, W, H);
      if (!rects) why = 'stage';
      else if (rects.length === 0) {
        // Nothing visible changed: an empty redraw.
        this.stats.partial++;
        this.stats.lastArea = 0;
        this.stats.lastRects = 0;
        this.stats.lastReason = '';
        this.bigInRow = 0;
        return { rects, bbox: { x0: 0, y0: 0, x1: 0, y1: 0 } };
      } else {
        const total = rects.reduce((a, r) => a + area(r), 0);
        if (total > MAX_AREA * W * H) {
          why = 'large';
          rects = null;
        } else {
          this.stats.partial++;
          this.stats.lastArea = total / Math.max(1, W * H);
          this.stats.lastRects = rects.length;
          this.stats.lastReason = '';
          this.bigInRow = 0;
          this.lastRects = rects;
          let bbox: Box | null = null;
          for (const r of rects) bbox = union(bbox, r);
          return { rects, bbox: bbox! };
        }
      }
    }
    // A whole frame.
    this.stats.full++;
    this.stats.lastArea = 1;
    this.stats.lastRects = 0;
    this.stats.lastReason = why;
    this.viewKey = viewKey;
    if (why === 'large' || why === 'changes') {
      if (++this.bigInRow >= SUSPEND_AFTER) {
        this.bigInRow = 0;
        this.pause = SUSPEND_RENDERS;
      }
    }
    if (this.pause > 0) {
      this.pause--;
      this.stats.suspended++;
      this.valid = false;
      return null;
    }
    this.measureAll(stage, vm);
    return null;
  }

  // ------------------------------------------------------------------ records

  /** Measures the whole stage: a new epoch of records. */
  private measureAll(stage: DisplayObject, vm: Mat): void {
    this.epoch++;
    this.bitmapUsers.clear();
    this.measure(stage, vm, true, false);
    this.valid = true;
  }

  private use(bd: object, o: DisplayObject): void {
    let s = this.bitmapUsers.get(bd);
    if (!s) this.bitmapUsers.set(bd, (s = new Set()));
    s.add(o);
  }

  /** Filter padding (device px) of `o`'s own filters, or null without any drawn filter. */
  private filterPad(o: DisplayObject): { x: number; y: number } | null {
    const fl = rd.filters(o);
    if (!fl.length) return null;
    let x = 0;
    let y = 0;
    let any = false;
    for (const f of fl) {
      const s = filterSpec(f);
      if (!s) continue;
      const p = filterPadding(s, this.fsx, this.fsy);
      x += p.x;
      y += p.y;
      any = true;
    }
    return any ? { x: x + 2, y: y + 2 } : null;
  }

  /**
   * Device box of what `o` draws under device matrix `m` (its own matrix included), recorded on `o` and its
   * descendants. `shown`: every ancestor is visible; `inMask`: inside a mask or clip layer (drawn whatever its
   * visibility).
   */
  private measure(o: DisplayObject, m: Mat, shown: boolean, inMask: boolean): Box | null {
    const masking = inMask || rd.isMask(o) || rd.clipDepth(o) > 0;
    o.$dgen = this.epoch;
    if (!masking && (!shown || !rd.isVisible(o))) {
      o.$dbox = null;
      o.$dkids = null;
      return null;
    }
    let box: Box | null = null;
    const kind = rd.renderKind(o);
    const sb = rd.selfBoundsWithMatrix(o, m);
    if (sb) box = { x0: sb.xMin, y0: sb.yMin, x1: sb.xMax, y1: sb.yMax };
    if (kind === 'shape' || kind === 'morph') {
      const info = rd.shapeImage(o);
      if (info) {
        const ref = info.ref;
        const sw = ref.sw ?? (info.image as any)?.width;
        const sh = ref.sh ?? (info.image as any)?.height;
        if (sw > 0 && sh > 0) {
          const k = 1 / (ref.scale || 1);
          const im: Mat = [m[0] * k, m[1] * k, m[2] * k, m[3] * k, m[0] * ref.offsetX + m[2] * ref.offsetY + m[4], m[1] * ref.offsetX + m[3] * ref.offsetY + m[5]];
          box = union(box, rectBox(im, 0, 0, sw, sh));
        }
      }
    } else if (kind === 'bitmap') {
      const b = rd.bitmapOf(o);
      if (b) this.use(b.bitmapData, o);
    }
    const g = rd.graphicsCommands(o);
    if (g) for (const c of g) if (c.op === 'beginBitmapFill' && (c as any).bitmap) this.use((c as any).bitmap, o);
    if (rd.opaqueBackground(o) !== null) {
      const b = rd.boundsWithMatrix(o, m);
      if (b) box = union(box, { x0: b.xMin, y0: b.yMin, x1: b.xMax, y1: b.yMax });
    }
    const kids = rd.renderChildren(o);
    if (kids.length || kind === 'container' || kind === 'stage' || kind === 'button') {
      const sr = rd.scrollRect(o);
      const cm = sr ? translate(m, -sr.x, -sr.y) : m;
      for (const c of kids) box = union(box, this.measure(c, mul(cm, rd.localMatrix(c)), true, masking));
      if (sr && box) {
        const clip = rectBox(m, 0, 0, sr.width, sr.height);
        box = { x0: Math.max(box.x0, clip.x0 - PAD), y0: Math.max(box.y0, clip.y0 - PAD), x1: Math.min(box.x1, clip.x1 + PAD), y1: Math.min(box.y1, clip.y1 + PAD) };
        if (!(box.x1 > box.x0 && box.y1 > box.y0)) box = null;
      }
      o.$dkids = kids.slice();
    } else o.$dkids = null;
    if (box) {
      const fp = this.filterPad(o);
      box = fp ? { x0: box.x0 - fp.x - PAD, y0: box.y0 - fp.y - PAD, x1: box.x1 + fp.x + PAD, y1: box.y1 + fp.y + PAD } : { x0: box.x0 - PAD, y0: box.y0 - PAD, x1: box.x1 + PAD, y1: box.y1 + PAD };
    }
    o.$dbox = box;
    return box;
  }

  /** The recorded box of `o` in this epoch (null: not drawn since the epoch began). */
  private record(o: DisplayObject): Box | null {
    return o.$dgen === this.epoch ? o.$dbox : null;
  }

  // ------------------------------------------------------------------ damage

  /**
   * Where `o` is: on the stage or not, its ancestors all visible (or a mask's), inside a mask, the device matrix of its
   * parent, the filter padding its ancestors add around what it draws.
   */
  private locate(o: DisplayObject): { onStage: boolean; shown: boolean; inMask: boolean; px: number; py: number } {
    let shown = true;
    let inMask = false;
    let px = 0;
    let py = 0;
    for (let p = rd.parentOf(o); p; p = rd.parentOf(p)) {
      if (rd.isStage(p)) return { onStage: true, shown, inMask, px, py };
      if (!rd.isVisible(p)) shown = false;
      if (rd.isMask(p) || rd.clipDepth(p) > 0) inMask = true;
      const fp = this.filterPad(p);
      if (fp) {
        px += fp.x;
        py += fp.y;
      }
    }
    return { onStage: false, shown, inMask, px, py };
  }

  /** Device matrix of `o` (its own matrix included) under the stage -> device matrix of the view. */
  private deviceMatrix(o: DisplayObject, vm: Mat): Mat {
    const p = rd.parentOf(o);
    let pm = p && !rd.isStage(p) ? mul(vm, rd.globalMatrix(p)) : vm;
    // (the parent's own scroll rect shifts its children: globalMatrix has only its ancestors')
    const sr = p ? rd.scrollRect(p) : null;
    if (sr) pm = translate(pm, -sr.x, -sr.y);
    return mul(pm, rd.localMatrix(o));
  }

  /** Measures `o` now (its subtree's records too) and grows its ancestors' records by the result. */
  private remeasure(o: DisplayObject, vm: Mat, at: { shown: boolean; inMask: boolean; px: number; py: number }): Box | null {
    const b = this.measure(o, this.deviceMatrix(o, vm), at.shown, at.inMask);
    if (b) {
      const grown = { x0: b.x0 - at.px, y0: b.y0 - at.py, x1: b.x1 + at.px, y1: b.y1 + at.py };
      for (let p = rd.parentOf(o); p && !rd.isStage(p); p = rd.parentOf(p)) {
        // (a record of nothing drawn too: a container whose first child just came)
        if (p.$dgen === this.epoch) p.$dbox = union(p.$dbox, grown);
      }
      return grown;
    }
    return null;
  }

  private vm: Mat = [1, 0, 0, 1, 0, 0];

  /**
   * The damage of the changes as device rectangles (empty: nothing visible changed), or null when the frame must be
   * drawn whole (the stage itself changed).
   */
  private damage(ch: rd.Changes, W: number, H: number): Box[] | null {
    const out: Box[] = [];
    const add = (b: Box | null, px = 0, py = 0) => {
      if (!b) return;
      const r = { x0: Math.max(0, Math.floor(b.x0 - px)), y0: Math.max(0, Math.floor(b.y0 - py)), x1: Math.min(W, Math.ceil(b.x1 + px)), y1: Math.min(H, Math.ceil(b.y1 + py)) };
      if (r.x1 > r.x0 && r.y1 > r.y0) out.push(r);
    };
    const vm = this.vm;
    // 1. The old places (before anything is measured again: a record read later may already be the new place).
    const later: Array<{ o: DisplayObject; whole: boolean; added: DisplayObject[]; at: ReturnType<DamageTracker['locate']> }> = [];
    for (let i = 0; i < ch.objects.length; i++) {
      const o = ch.objects[i];
      if (rd.isStage(o)) return null;
      const at = this.locate(o);
      let whole = (ch.flags[i] & DAMAGE_SELF) !== 0;
      if (whole) add(this.record(o), at.px, at.py);
      if (!at.onStage) {
        // (a removal damaged it through its old parent's child list; a mask kept off the display list still masks)
        const masked = rd.maskeeOf(o);
        if (masked) add(this.record(masked), this.locate(masked).px, this.locate(masked).py);
        continue;
      }
      let added: DisplayObject[] = [];
      if (!whole && ch.flags[i] & DAMAGE_CHILDREN) {
        const prev = o.$dgen === this.epoch ? o.$dkids : null;
        const cur = rd.renderChildren(o);
        if (!prev) {
          // Not drawn since the epoch began (hidden then): nothing old to damage, all of it new.
          whole = true;
        } else if (prev.length !== cur.length || prev.some((c, k) => c !== cur[k])) {
          const was = new Set(prev);
          const now = new Set(cur);
          const removed = prev.filter((c) => !now.has(c));
          added = cur.filter((c) => !was.has(c));
          // Reordered children, or a timeline clip layer (masking its siblings) coming or going: the container whole.
          const keptPrev = prev.filter((c) => now.has(c));
          const keptCur = cur.filter((c) => was.has(c));
          if (keptPrev.some((c, k) => c !== keptCur[k]) || removed.some((c) => rd.clipDepth(c) > 0) || added.some((c) => rd.clipDepth(c) > 0)) {
            whole = true;
            add(this.record(o), at.px, at.py);
          } else {
            // (inside the container's own filters too)
            const fp = this.filterPad(o);
            for (const c of removed) add(this.record(c), at.px + (fp?.x ?? 0), at.py + (fp?.y ?? 0));
          }
        }
      }
      later.push({ o, whole, added, at });
    }
    // The pixels of changed bitmaps where they are drawn (their Bitmaps do not move: old place = new place).
    for (const bd of ch.bitmaps) {
      const users = this.bitmapUsers.get(bd);
      if (!users) continue; // not drawn in this epoch (a Bitmap showing it now is a change of its own)
      for (const u of users) {
        const r = this.record(u);
        if (!r) continue;
        const at = this.locate(u);
        if (!at.onStage) continue;
        const rects: Array<[number, number, number, number]> | null = rd.renderKind(u) === 'bitmap' && !bd.$disposed && bd.$dirty ? (bd.$dirtyRects.length ? bd.$dirtyRects : [bd.$dirty]) : null;
        if (!rects) {
          add(r, at.px, at.py);
          continue;
        }
        // The changed texels' device box, grown by the sampling footprint (sharp bilinear reads a texel around).
        const m = this.deviceMatrix(u, vm);
        const tp = Math.ceil(Math.max(Math.hypot(m[0], m[1]), Math.hypot(m[2], m[3]))) + PAD;
        for (const d of rects) {
          const b = rectBox(m, d[0], d[1], d[2] - d[0], d[3] - d[1]);
          add({ x0: Math.max(r.x0, b.x0 - tp), y0: Math.max(r.y0, b.y0 - tp), x1: Math.min(r.x1, b.x1 + tp), y1: Math.min(r.y1, b.y1 + tp) }, at.px, at.py);
        }
      }
    }
    // 2. The new places.
    for (const { o, whole, added, at } of later) {
      if (whole) add(this.remeasure(o, vm, at));
      else {
        const fp = added.length ? this.filterPad(o) : null;
        const inner = { shown: at.shown && rd.isVisible(o), inMask: at.inMask || rd.isMask(o) || rd.clipDepth(o) > 0, px: at.px + (fp?.x ?? 0), py: at.py + (fp?.y ?? 0) };
        for (const c of added) add(this.remeasure(c, vm, inner));
        if (o.$dgen === this.epoch && o.$dkids) o.$dkids = rd.renderChildren(o).slice();
      }
    }
    return merge(out, W, H);
  }

  /** Stage -> device matrix of the frame being planned (set by the renderer before plan()). */
  setView(vm: Mat): void {
    this.vm = vm;
  }
}

/** Merges overlapping or close rectangles, then the cheapest pairs until at most MAX_RECTS are left. */
function merge(rs: Box[], W: number, H: number): Box[] {
  if (rs.length > 64) {
    let b: Box | null = null;
    for (const r of rs) b = union(b, r);
    return b ? [b] : [];
  }
  const out = rs.slice();
  const near = (a: Box, b: Box) => a.x0 <= b.x1 + MERGE_GAP && b.x0 <= a.x1 + MERGE_GAP && a.y0 <= b.y1 + MERGE_GAP && b.y0 <= a.y1 + MERGE_GAP;
  let changed = true;
  while (changed) {
    changed = false;
    for (let i = 0; i < out.length && !changed; i++)
      for (let j = i + 1; j < out.length; j++)
        if (near(out[i], out[j])) {
          out[i] = union(out[i], out[j])!;
          out.splice(j, 1);
          changed = true;
          break;
        }
  }
  while (out.length > MAX_RECTS) {
    let bi = 0;
    let bj = 1;
    let best = Infinity;
    for (let i = 0; i < out.length; i++)
      for (let j = i + 1; j < out.length; j++) {
        const cost = area(union(out[i], out[j])!) - area(out[i]) - area(out[j]);
        if (cost < best) {
          best = cost;
          bi = i;
          bj = j;
        }
      }
    out[bi] = union(out[bi], out[bj])!;
    out.splice(bj, 1);
  }
  void W;
  void H;
  return out;
}
